import { forwardRef, type ComponentProps, type ReactNode } from "react";
import { cn } from "@rekati/utils";

/**
 * Cult UI's Halo Input shell, restyled flat: a hairline field that takes an
 * ink edge and a soft yellow ring while it has focus. The control inside
 * keeps its own semantics.
 */
export function HaloShell({
  children,
  invalid,
  className,
  size = "md",
  leading,
}: {
  children: ReactNode;
  invalid?: boolean;
  className?: string;
  size?: "md" | "lg";
  leading?: ReactNode;
}) {
  return (
    <div
      data-invalid={invalid || undefined}
      className={cn(
        "min-w-0 rounded-xl border border-input bg-card transition-[border-color,box-shadow] duration-150",
        "focus-within:border-foreground focus-within:ring-4 focus-within:ring-volt/35",
        "data-invalid:border-destructive data-invalid:focus-within:ring-destructive/20",
        className,
      )}
    >
      <div
        className={cn("flex items-center gap-3", size === "lg" ? "min-h-16 px-5 py-3" : "min-h-12 px-4 py-2")}
      >
        {leading ? (
          <span className="flex shrink-0 items-center text-muted-foreground [&_svg]:size-5">{leading}</span>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/**
 * The bare control that sits inside a HaloShell: no border, no outline of its own.
 * `w-full` drops the input's intrinsic ~20-character width, which would
 * otherwise push a narrow card wider than a phone.
 */
export const haloControlClass =
  "w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-foreground outline-none placeholder:text-muted-foreground/60 focus-visible:outline-none";

export const HaloInput = forwardRef<
  HTMLInputElement,
  ComponentProps<"input"> & { invalid?: boolean; shellClassName?: string; leading?: ReactNode }
>(function HaloInput({ invalid, shellClassName, leading, className, ...props }, ref) {
  return (
    <HaloShell invalid={invalid} className={shellClassName} leading={leading}>
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(haloControlClass, "h-8 text-base", className)}
        {...props}
      />
    </HaloShell>
  );
});

export const HaloTextarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<"textarea"> & { invalid?: boolean; shellClassName?: string }
>(function HaloTextarea({ invalid, shellClassName, className, rows = 3, ...props }, ref) {
  return (
    <HaloShell invalid={invalid} className={shellClassName}>
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={invalid || undefined}
        className={cn(
          haloControlClass,
          "field-sizing-content min-h-[5rem] resize-y py-1 text-base",
          className,
        )}
        {...props}
      />
    </HaloShell>
  );
});
